import clsx from 'clsx';
import { Outlet } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { Group, Panel, useDefaultLayout, useGroupRef, usePanelRef, type Layout as PanelLayout } from 'react-resizable-panels';

import { leftSidebarCollapsedAtom } from '@/state';
import { useRouteInfo } from '@/hooks/use-route-info';
import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';

import { Separator } from '@/components/separator';
import { AppToaster } from '@/components/app-toaster';
import { AsideContent } from '@/features/aside-content';
import { EmptyLayout } from '@/components/empty-layout';
import { NavigationRail } from '@/components/navigation-rail';
import { SidebarToggle } from '@/components/sidebar-toggle';
import { ResizablePanel } from '@/components/resizable-panel';
import { DocuAiDockSlot } from './docuai-dock-slot';
import { AgentRunNotifications } from './agent-run-notifications';
import { SaveBeforeLogoutModal } from '@/components/save-before-logout-modal';

import styles from './layout.module.scss';

const LEFT_PANEL_WIDTH = 346;
const RIGHT_PANEL_WIDTH = 360;
const MAIN_PANEL_MIN_WIDTH = 652;
const RESIZABLE_LAYOUT_MIN_WIDTH = 1600;
const NAVIGATION_RAIL_WIDTH = 58;
// Bumped away from the unsuffixed key (the bare routeKey) rather than reusing it: that key saved
// without `onlySaveAfterUserInteractions`, so nearly every entry under it is the old hard-coded
// initial layout, auto-saved on first paint, and those entries must never be read. The few that hold a
// real drag on a wide screen are knowingly dropped rather than migrated: nothing in the stored value
// tells a clamped default apart from a deliberate drag.
const LAYOUT_STORAGE_VERSION = 'v2';
// Outlasts the collapse transition in layout.module.scss, which only runs while this flag is set:
// a drag must never be eased.
const COLLAPSE_ANIMATION_MS = 260;

export const Layout = () => {
  const { isHome, isLibrary, isTags, isArchive, isCortex, isSettings, routeKey, leftSidebar, rightSidebar } = useRouteInfo();
  const groupRef = useGroupRef();
  const leftPanelRef = usePanelRef();
  const groupElementRef = useRef<HTMLDivElement | null>(null);
  const leftPanelElementRef = useRef<HTMLDivElement | null>(null);
  const expandTabRef = useRef<HTMLDivElement | null>(null);
  const leftExpandedWidth = useRef(LEFT_PANEL_WIDTH);
  const collapseAnimationTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const focusBeforeCollapse = useRef<HTMLElement | null>(null);

  const { ref: rootLayoutRef, clientWidth } = useElementResize();

  const leftVisible = !!leftSidebar?.visible;
  const rightVisible = !!rightSidebar?.visible;
  const canResizeByWidth = clientWidth > RESIZABLE_LAYOUT_MIN_WIDTH;
  const leftResizable = !!leftSidebar?.resizable && canResizeByWidth;
  const rightResizable = !!rightSidebar?.resizable && canResizeByWidth;

  const leftCollapsible = leftVisible && !!leftSidebar?.collapsible;
  const [collapsedByUser, setCollapsedByUser] = useAtom(leftSidebarCollapsedAtom);
  const leftCollapsed = leftCollapsible && collapsedByUser;

  // Narrower than the panels' combined pixel minimums, the library has to break one of them, and it
  // picks the collapsible panel: an expanded sidebar was crushed to nothing on laptop-sized windows.
  // Minimums that are the panels' shares of that sum always fit, and keep all three shrinking together.
  //
  // A hidden right panel sits under the main placeholder and needs no width at all; holding it to its
  // minimum would shrink the left sidebar on a window that has room for everything actually on screen.
  const rightPanelMinWidth = rightVisible ? RIGHT_PANEL_WIDTH : 0;
  const totalMinWidth = LEFT_PANEL_WIDTH + MAIN_PANEL_MIN_WIDTH + rightPanelMinWidth;
  const fitsPanelMinimums = !clientWidth || clientWidth - NAVIGATION_RAIL_WIDTH >= totalMinWidth;
  const minShareOf = (width: number) => Math.floor((width / totalMinWidth) * 10000) / 100;
  const minSizeOf = (width: number) => (fitsPanelMinimums ? width : `${minShareOf(width)}%`);
  const leftPanelMinSize = minSizeOf(LEFT_PANEL_WIDTH);

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: `${routeKey}:${LAYOUT_STORAGE_VERSION}`,
    storage: localStorage,
    panelIds: ['left-panel', 'main-panel', 'right-panel'],
    onlySaveAfterUserInteractions: true,
  });

  useEffect(() => {
    if (defaultLayout) {
      groupRef.current?.setLayout(defaultLayout);
    }
  }, [defaultLayout, routeKey, groupRef]);

  // Only the fallback tracks the measured width; re-applying a stored percentage layout on every
  // resize tick would defeat the panels' `preserve-pixel-size` behaviour.
  useEffect(() => {
    if (defaultLayout) return;

    const layout = getFallbackLayout(clientWidth, leftCollapsed, rightVisible);
    if (layout) {
      groupRef.current?.setLayout(layout);
    }
  }, [defaultLayout, clientWidth, leftCollapsed, rightVisible, routeKey, groupRef]);

  // Declared after the layout effects on purpose: they restore the panel widths, and this then
  // re-applies the collapse on top, so `expand()` later returns to the width that was just restored.
  const previousCollapsedByUser = useRef(collapsedByUser);
  useEffect(() => {
    // Only the user's own toggle animates and moves focus. `leftCollapsed` also flips when a route
    // change takes the sidebar in or out of collapsible territory, and a page should simply arrive in
    // its state rather than replay the collapse on every navigation.
    const toggled = previousCollapsedByUser.current !== collapsedByUser;
    previousCollapsedByUser.current = collapsedByUser;

    const groupElement = groupElementRef.current;
    const rootLayout = rootLayoutRef.current;
    if (toggled && groupElement && rootLayout) {
      // The sidebar content keeps its expanded width while the panel edge sweeps across it; left to
      // follow the panel, it would re-wrap every frame and read as a second, separate animation.
      groupElement.style.setProperty('--left-panel-content-width', `${leftExpandedWidth.current}px`);
      // On the root rather than the group: the expand tab and the main placeholder, the group's
      // siblings, ease on the same flag.
      rootLayout.dataset.leftCollapseAnimating = 'true';

      // Not this effect's cleanup: the effect also re-runs on a window resize or a saved layout, and a
      // cleanup would cancel the timer there with nothing left to end the animation.
      clearTimeout(collapseAnimationTimeout.current);
      collapseAnimationTimeout.current = setTimeout(() => delete rootLayout.dataset.leftCollapseAnimating, COLLAPSE_ANIMATION_MS);
    }

    if (leftCollapsed) {
      leftPanelRef.current?.collapse();
    } else {
      // `expand()` only knows the width an imperative `collapse()` recorded. A panel restored shut from a
      // saved layout would reopen at its minimum, so the width is put back here.
      const wasCollapsed = leftPanelRef.current?.isCollapsed();
      leftPanelRef.current?.expand();
      if (wasCollapsed) {
        leftPanelRef.current?.resize(leftExpandedWidth.current);
      }
    }

    // The separator beside the sidebar goes disabled with it but can keep focus, and the library still
    // resizes from a focused separator's keys, which would reopen the sidebar while it counts as
    // collapsed. It is a control the user was operating, so like a toggle it hands over to the expand tab.
    const leftSeparator = leftPanelElementRef.current?.nextElementSibling;
    if (toggled && leftCollapsed && leftSeparator && leftSeparator === document.activeElement) {
      expandTabRef.current?.querySelector<HTMLElement>('[data-sidebar-toggle]')?.focus();
    } else if (toggled && leftCollapsed) {
      focusBeforeCollapse.current = moveFocus(leftPanelElementRef.current, expandTabRef.current);
    } else if (toggled) {
      moveFocus(expandTabRef.current, leftPanelElementRef.current, focusBeforeCollapse.current);
      focusBeforeCollapse.current = null;
    }
  }, [leftCollapsed, collapsedByUser, defaultLayout, clientWidth, routeKey, leftPanelRef, rootLayoutRef]);

  useEffect(() => () => clearTimeout(collapseAnimationTimeout.current), []);

  useEffect(() => {
    if (!leftCollapsible) return;

    const toggleOnShortcut = (event: KeyboardEvent) => {
      // `code` as well as `key`: on layouts that type a backslash through AltGr (which itself reports as
      // Ctrl+Alt, so it cannot be told from the shortcut), the physical key is the only one reachable.
      const isBackslash = event.key === '\\' || event.code === 'Backslash';
      if (event.repeat || !isBackslash || !(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;

      // Modals and overlays are portalled outside the layout; while focus is in one, the shortcut
      // must not rearrange the page underneath it.
      const { target } = event;
      if (target instanceof Node && target !== document.body && !rootLayoutRef.current?.contains(target)) return;

      event.preventDefault();
      setCollapsedByUser((collapsed) => !collapsed);
    };

    window.addEventListener('keydown', toggleOnShortcut);
    return () => window.removeEventListener('keydown', toggleOnShortcut);
  }, [leftCollapsible, setCollapsedByUser, rootLayoutRef]);

  useEffect(() => {
    !leftSidebar && setMainPlaceholderLeft(rootLayoutRef.current, 0);
  }, [leftSidebar, rootLayoutRef]);

  const main = (
    <main
      className={clsx({
        [styles.tags]: isTags,
        [styles.cortex]: isCortex,
        [styles.archive]: isArchive,
        [styles.settings]: isSettings,
        [styles.notes]: isHome || isLibrary,
      })}
    >
      <EmptyLayout />
      <Outlet />
    </main>
  );

  return (
    <>
      <div ref={rootLayoutRef} className={styles.rootLayout}>
        <NavigationRail />
        {/* Slides out from under the rail at the height of the sidebar header's own toggle, so the control
            seems to travel with the collapsing edge and nothing in the rail has to move for it. */}
        <div ref={expandTabRef} className={styles.sidebarExpandTab} data-visible={leftCollapsed || undefined}>
          <SidebarToggle tooltipPlacement="right" className={styles.sidebarExpandButton} testId="left-sidebar-expand-tab" />
        </div>
        <Group
          id={`${routeKey}:${LAYOUT_STORAGE_VERSION}`}
          disableCursor
          groupRef={groupRef}
          elementRef={groupElementRef}
          orientation="horizontal"
          className={styles.panelGroup}
          defaultLayout={defaultLayout}
          data-floating-chat-canvas
          onLayoutChange={(layout) => {
            // Collapsing is the toggle's job, not the drag's: a `collapsible` panel (which the imperative
            // collapse needs) snaps shut when dragged past half its minimum. Put back at the minimum in the
            // same pointer event, before a frame is painted, the drag simply stops there. Later moves are
            // computed from where the drag started, so each one that snaps is held again.
            if (!leftCollapsible || leftCollapsed || layout['left-panel'] !== 0) return;

            const groupWidth = getGroupWidth(groupElementRef.current);
            if (!groupWidth) return;

            const left = fitsPanelMinimums ? (LEFT_PANEL_WIDTH / groupWidth) * 100 : minShareOf(LEFT_PANEL_WIDTH);
            groupRef.current?.setLayout({ ...layout, 'left-panel': left, 'main-panel': layout['main-panel'] - left });
          }}
          onLayoutChanged={(reportedLayout, meta) => {
            // The snap that `onLayoutChange` held open can still be reported here after the held layout,
            // from the same library update (a keyboard resize does this). It is stale, but it carries the
            // user interaction that the held layout, set programmatically, did not: the held layout is what
            // gets saved, under it.
            const heldOpen = leftCollapsible && !leftCollapsed && reportedLayout['left-panel'] === 0;
            const heldLayout = heldOpen ? groupRef.current?.getLayout() : undefined;
            const layout = heldLayout?.['left-panel'] ? heldLayout : reportedLayout;

            const groupWidth = getGroupWidth(groupElementRef.current);
            onLayoutChanged(withExpandedLeftPanel(layout, leftExpandedWidth.current, groupWidth), meta);

            // This fires once a drag has ended, never during it. Taken from the layout rather than the element,
            // which is still mid-transition when a programmatic expand reports in.
            if (layout['left-panel'] > 0 && groupWidth > 0) {
              leftExpandedWidth.current = Math.round((layout['left-panel'] / 100) * groupWidth);
            }
          }}
          disabled={!leftResizable && !rightResizable}
        >
          <ResizablePanel
            groupResizeBehavior="preserve-pixel-size"
            id="left-panel"
            panelRef={leftPanelRef}
            collapsible={leftCollapsible}
            collapsedSize={0}
            minSize={leftPanelMinSize}
            inert={leftCollapsed}
            defaultSize={LEFT_PANEL_WIDTH}
            className={styles.leftPanel}
            data-left-panel-visible={leftVisible}
            elementRef={leftPanelElementRef}
            onResize={(panelSize) => {
              leftSidebar && setMainPlaceholderLeft(rootLayoutRef.current, panelSize.inPixels);
              // The clip follows the live width, which is what the stylesheet needs: it lands the frame the
              // collapse animation reaches zero, and lifts the frame an expand leaves it.
              leftPanelElementRef.current?.toggleAttribute('data-zero-width', panelSize.inPixels === 0);
            }}
          >
            <AsideContent />
          </ResizablePanel>
          <Separator
            visible={leftVisible}
            disabled={!leftResizable || leftCollapsed}
            collapsed={leftCollapsed}
            onExpand={() => setCollapsedByUser(false)}
            leftPanelMinWidth={LEFT_PANEL_WIDTH}
            rightPanelMinWidth={MAIN_PANEL_MIN_WIDTH}
          />
          <Panel id="main-panel" minSize={minSizeOf(MAIN_PANEL_MIN_WIDTH)}>
            {rightVisible && main}
          </Panel>
          <Separator disabled={!rightResizable} leftPanelMinWidth={MAIN_PANEL_MIN_WIDTH} rightPanelMinWidth={RIGHT_PANEL_WIDTH} />
          <ResizablePanel
            id="right-panel"
            minSize={minSizeOf(rightPanelMinWidth)}
            defaultSize={RIGHT_PANEL_WIDTH}
            position="right"
            className={styles.rightPanel}
            groupResizeBehavior="preserve-pixel-size"
            data-right-panel-visible={rightVisible}
            data-floating-chat-dock-column={rightVisible ? 'layout' : undefined}
          >
            <div id="layout-right-sidebar-content" className={styles.rightSidebarContent} />
            <DocuAiDockSlot column="layout" />
          </ResizablePanel>
        </Group>
        {!rightVisible && (
          <div className={styles.mainPlaceholder} data-left-collapsed={leftCollapsed || undefined}>
            {main}
            <div className={styles.virtualDockColumn} data-floating-chat-dock-column="layout">
              <DocuAiDockSlot column="layout" />
            </div>
          </div>
        )}
      </div>
      <SaveBeforeLogoutModal />
      <AgentRunNotifications />
      <AppToaster />
    </>
  );
};

/**
 * Percentages matching the panels' pixel `defaultSize` props at the measured width; `setLayout` only
 * takes percentages. Unmeasured (0) resets nothing: the panels' own defaults govern the first paint,
 * and the reset would otherwise jump once the measurement arrives.
 */
const getFallbackLayout = (clientWidth: number, leftCollapsed: boolean, rightVisible: boolean) => {
  if (!clientWidth) return null;

  // A collapsed left panel takes no width, so it must not claim a share either: counting it would
  // squeeze the right panel below its pixel width on a window that still has room for it.
  const leftWidth = leftCollapsed ? 0 : LEFT_PANEL_WIDTH;
  // A hidden right panel lies under the main placeholder, so it only takes what the visible columns
  // leave over. On a window with room it keeps its usual width, so nothing changes there.
  const rightWidth = rightVisible
    ? RIGHT_PANEL_WIDTH
    : Math.min(RIGHT_PANEL_WIDTH, Math.max(0, clientWidth - leftWidth - MAIN_PANEL_MIN_WIDTH));
  const totalWidth = leftWidth + MAIN_PANEL_MIN_WIDTH + rightWidth;

  // The narrowest layout that still fits every open panel: below it the pixel widths cannot all be
  // honoured, so their share of this sum is the closest the ratio can get.
  if (clientWidth < totalWidth) {
    return {
      'left-panel': (leftWidth / totalWidth) * 100,
      'main-panel': (MAIN_PANEL_MIN_WIDTH / totalWidth) * 100,
      'right-panel': (rightWidth / totalWidth) * 100,
    };
  }

  const left = (leftWidth / clientWidth) * 100;
  const right = (rightWidth / clientWidth) * 100;

  return { 'left-panel': left, 'main-panel': 100 - left - right, 'right-panel': right };
};

const getGroupWidth = (group: HTMLElement | null) => group?.offsetWidth ?? 0;

/**
 * The layout to persist. A collapse reports the left panel at 0, and saving that would lose the width it
 * had: after a reload, or on the next visit to the route, the sidebar could only reopen at its minimum.
 * The collapsed state is carried by the collapsed flag alone, so the saved widths keep the left panel at
 * its last expanded width, taken back out of the main panel that absorbed it.
 */
const withExpandedLeftPanel = (layout: PanelLayout, leftExpandedWidth: number, groupWidth: number) => {
  if (layout['left-panel'] !== 0 || !groupWidth) return layout;

  const left = Math.min((leftExpandedWidth / groupWidth) * 100, layout['main-panel']);

  return { ...layout, 'left-panel': left, 'main-panel': layout['main-panel'] - left };
};

/**
 * A toggle leaves whatever had focus unreachable: the sidebar goes inert on collapse, the tab slides back
 * under the rail on expand. Focus only moves when it was in `from`, so a shortcut pressed in the editor
 * steals nothing. From a toggle button it goes to the counterpart toggle, which keeps a keyboard user on
 * the control they are operating. From anywhere else (the sidebar search, via the shortcut) it is simply
 * released, since landing on a toggle would pop its focus tooltip over the note, and the element is
 * returned so expanding can give focus back to it.
 */
const moveFocus = (from: HTMLElement | null, to: HTMLElement | null, restore?: HTMLElement | null) => {
  const active = document.activeElement;

  if (restore?.isConnected && (!active || active === document.body)) {
    restore.focus();
    return null;
  }

  if (!(active instanceof HTMLElement) || !from?.contains(active)) return null;

  if (active.matches('[data-sidebar-toggle]')) {
    to?.querySelector<HTMLElement>('[data-sidebar-toggle]')?.focus();
    return null;
  }

  active.blur();
  return active;
};

/**
 * Written straight to the DOM: the panel reports its width from a ResizeObserver, once per frame through
 * a drag or a collapse, and only a write in that same callback keeps the placeholder on the panel's edge.
 * Through React state it trails by a frame.
 */
const setMainPlaceholderLeft = (rootLayout: HTMLElement | null, left: number) => {
  rootLayout?.style.setProperty('--left', `${left}px`);
};
